import type { ShipOrderClient, ShipyardBlocker, ShipyardQuote } from '@galaxis/shared';
import { formatGameDuration } from '@galaxis/shared';

/** Hourly upkeep line: "+12 energia · +5 fém / óra" (nothing when the ship adds none). */
export function upkeepLabel(delta: ShipyardQuote['upkeepDelta']): string {
  const parts: string[] = [];
  if (delta.energy !== 0) parts.push(`${delta.energy > 0 ? '+' : ''}${delta.energy} energia`);
  if (delta.metal !== 0) parts.push(`${delta.metal > 0 ? '+' : ''}${delta.metal} fém`);
  return parts.length === 0 ? 'nincs többletfenntartás' : `${parts.join(' · ')} / óra`;
}

/** Short reason why an order cannot be placed, from the server's blockers and shortfall. */
export function shortfallLabel(q: Pick<ShipyardQuote, 'shortfall'>): string | null {
  const parts: string[] = [];
  if (q.shortfall.metal > 0) parts.push(`${q.shortfall.metal} fém`);
  if (q.shortfall.energy > 0) parts.push(`${q.shortfall.energy} energia`);
  return parts.length === 0 ? null : `Hiányzik: ${parts.join(' és ')}`;
}

/** Blockers to show: the resource one is replaced by the concrete shortfall. */
export function blockerLines(q: Pick<ShipyardQuote, 'blockers' | 'shortfall'>): string[] {
  return q.blockers.map((b: ShipyardBlocker) => (b.code === 'insufficient_resources' ? shortfallLabel(q) ?? b.message : b.message));
}

export function remainingLabel(order: Pick<ShipOrderClient, 'completesAt'>, now: number): string {
  if (!order.completesAt) return '—';
  return formatGameDuration(Math.max(0, new Date(order.completesAt).getTime() - now));
}

export function progressOf(order: Pick<ShipOrderClient, 'startedAt' | 'completesAt'>, now: number): number {
  if (!order.completesAt) return 0;
  const start = new Date(order.startedAt).getTime();
  const end = new Date(order.completesAt).getTime();
  return end > start ? Math.min(1, Math.max(0, (now - start) / (end - start))) : 1;
}

/** Orders that existed before and are gone now = finished ships. */
export function finishedOrders<T extends { id: string }>(prev: readonly T[], next: readonly { id: string }[]): T[] {
  const ids = new Set(next.map(o => o.id));
  return prev.filter(o => !ids.has(o.id));
}
